import { createAsyncThunk } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/redux/store'
import WebSocketManager from '@/services/websocketManager'
import { VisibilityStatus } from '@/redux/slices/studentDecisionWebsocketSlice'

// Get the websocket manager instance
const wsManager = WebSocketManager.getInstance()

export const connectDecisionWebSocket = createAsyncThunk<
  void,
  {
    jwtToken: string
    roomId: string
    phaseId?: string
    impersonatePlayerId?: string
  },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/connectStudentDecision',
  async (
    { jwtToken, roomId, phaseId, impersonatePlayerId },
    { rejectWithValue }
  ) => {
    try {
      return await wsManager.connectToStudentDecision(
        jwtToken,
        roomId,
        phaseId,
        impersonatePlayerId
      )
    } catch (error) {
      return rejectWithValue(
        (error as Error).message ||
          'Failed to connect to student decision websocket'
      )
    }
  }
)

export const disconnectDecisionWebSocket = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>('websocket/disconnectStudentDecision', async () => {
  return await wsManager.disconnect('studentDecision')
})

export const submitStudentDecision = createAsyncThunk<
  void,
  {
    studentId: number | null
    roomId: string
    decision: string
    lateSubmission: boolean
    phaseId: number
    isPublic: boolean
    teamId?: number | null
    selectedResearchOptionIndex?: number | null
    decisionJustification?: string | null
  },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/submitStudentDecision',
  async (
    {
      studentId,
      roomId,
      decision,
      lateSubmission,
      phaseId,
      isPublic,
      teamId,
      selectedResearchOptionIndex,
      decisionJustification,
    },
    { rejectWithValue }
  ) => {
    const message: any = {
      type: 'submit_decision',
      student_id: studentId,
      room_id: roomId,
      decision,
      late_submission: lateSubmission,
      phase_id: phaseId,
      is_public: isPublic,
    }

    // Include team_id if in team mode
    if (teamId !== null && teamId !== undefined) {
      message.team_id = teamId
    }
    if (
      selectedResearchOptionIndex !== null &&
      selectedResearchOptionIndex !== undefined
    ) {
      message.selected_research_option_index = selectedResearchOptionIndex
    }
    if (decisionJustification !== null && decisionJustification !== undefined) {
      message.decision_justification = decisionJustification
    }

    const success = wsManager.sendMessage('studentDecision', message)

    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const adjudicateDecision = createAsyncThunk<
  void,
  { decisionId: string; adjudication: string },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/adjudicateDecision',
  async ({ decisionId, adjudication }, { rejectWithValue }) => {
    const success = wsManager.sendMessage('studentDecision', {
      type: 'add_adjudication',
      decision_id: decisionId,
      adjudication,
    })
    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export const approveDecision = createAsyncThunk<
  void,
  {
    decisionId: string
    approvalStatus: VisibilityStatus
    formattedDecision?: string
  },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/approveDecision',
  async (
    { decisionId, approvalStatus, formattedDecision },
    { rejectWithValue }
  ) => {
    const success = wsManager.sendMessage('studentDecision', {
      type: 'approval_status',
      decision_id: decisionId,
      approval_status: approvalStatus,
      formatted_decision: formattedDecision,
    })
    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)

export interface BulkAdjudicationData {
  decision_id: string
  adjudication: string
  approvalStatus?: VisibilityStatus
  formattedDecision?: string
}

export const bulkAdjudicateDecisions = createAsyncThunk<
  void,
  { adjudications: BulkAdjudicationData[] },
  { dispatch: AppDispatch; state: RootState }
>(
  'websocket/bulkAdjudicateDecisions',
  async ({ adjudications }, { rejectWithValue }) => {
    const success = wsManager.sendMessage('studentDecision', {
      type: 'bulk_adjudicate',
      adjudications: adjudications,
    })
    if (!success) {
      return rejectWithValue('WebSocket is not connected')
    }
  }
)
